'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import { ColorMode } from '@/constants';
import { copyToClipboard } from '@/utils';
import useInterval from '@/hooks/use-interval';

import CopyButton from './CopyButton';

interface Props {
  code: string;
  lockedToColorMode?: ColorMode | false;
  children: React.ReactNode;
}

function WithCopyButton({
  code,
  lockedToColorMode,
  children,
}: Props) {
  const [copyIds, setCopyIds] = React.useState<Array<number>>([]);

  // Clean up old copy IDs, to prevent infinite memory buildup.
  useInterval(() => {
    if (copyIds.length === 0) {
      return;
    }

    setCopyIds((currentValue) => {
      return currentValue.filter((id) => Date.now() - id < 5000);
    });
  }, 5000);

  const handleCopy = React.useCallback(() => {
    copyToClipboard(code);
    setCopyIds((currentValue) => [...currentValue, Date.now()]);
  }, [code]);

  return (
    <>
      <CopyButton
        handleCopy={handleCopy}
        data-locked-to-color-mode={lockedToColorMode}
      />

      {children}

      {copyIds.map((id) => (
        <CopyEffectWrapper key={id}>
          <CopyEffect data-locked-to-color-mode={lockedToColorMode} />
        </CopyEffectWrapper>
      ))}
    </>
  );
}

const CopyEffectWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  overflow: clip;
  pointer-events: none;
`;

const CopyEffect = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    transparent 30%,
    var(--copy-color) 48%,
    var(--copy-color) 52%,
    transparent 70%
  );
  animation:
    copy 800ms both ease-in-out,
    fadeInOut 1000ms ease-in-out both;
  will-change: transform;
  mix-blend-mode: var(--blend-mode);

  /*
    The color mode can be set in two ways:
    • The global page-level value, on the <html> tag
    • A local value, on this element via data-locked-to-color-mode

    This complicated selector ensures that we prioritize the local value, while still following the global value otherwise
  */
  html[data-color-mode='light']
    &:not([data-locked-to-color-mode='dark']),
  &[data-locked-to-color-mode='light'] {
    --copy-color: hsl(0deg 0% 100% / 0.75);
    --blend-mode: overlay;
  }
  html[data-color-mode='dark']
    &:not([data-locked-to-color-mode='light']),
  &[data-locked-to-color-mode='dark'] {
    --copy-color: hsl(210deg 15% 60% / 0.2);
    --blend-mode: lighten;
  }

  @keyframes copy {
    0% {
      transform: translateX(-50%);
    }
    100% {
      transform: translateX(50%);
    }
  }
  @keyframes fadeInOut {
    0% {
      opacity: 0;
    }
    20% {
      opacity: 1;
    }
    60% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

export default WithCopyButton;
